import Decimal from 'decimal.js';
import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import { prettifyPriceWithCurrencySymbol } from '@hotjar/webapp/billing/cost';

export interface BillingCycleDisplayCosts {
  payableAmount: string;
  hasPayableAmount: boolean;
  discountedMonthlyAmount: string;
  savingsAmount: string;
  savingsPercentage: number;
  futureSavingsPercentage: number | null;
}

export interface DisclaimerDisplayCosts {
  isVisible: boolean;
  totalCurrent: string;
  totalFuture: string | null;
  isProrated: boolean;
}

export const getBillingCycleDisplayCosts = ({
  summary,
  duration,
  futureSavingsPercentage,
  currencySymbol,
}: {
  summary: PriceSummary;
  duration: number;
  futureSavingsPercentage: number | null;
  currencySymbol: string | undefined;
}): BillingCycleDisplayCosts => {
  const prettifyPrice = prettifyPriceWithCurrencySymbol(currencySymbol);
  const payableAmount = summary.subtotal.minus(summary.discountTotal);

  return {
    hasPayableAmount: summary.total.gt(0),
    payableAmount: prettifyPrice(payableAmount),
    discountedMonthlyAmount: prettifyPrice(payableAmount.div(duration) ?? new Decimal('0')),
    savingsAmount: prettifyPrice(summary.discountTotal),
    savingsPercentage: summary.discountPercentage ?? 0,
    futureSavingsPercentage: futureSavingsPercentage ?? 0,
  };
};

export const getDisclaimerDisplayCosts = ({
  currentTotal,
  futureTotal,
  isProrated,
  currencySymbol,
}: {
  currentTotal: Decimal;
  futureTotal: Decimal;
  isProrated: boolean;
  currencySymbol: string | undefined;
}): DisclaimerDisplayCosts => {
  const prettifyPrice = prettifyPriceWithCurrencySymbol(currencySymbol);

  return {
    isVisible: currentTotal.gt('0'),
    isProrated,
    totalCurrent: prettifyPrice(currentTotal),
    totalFuture: isProrated ? prettifyPrice(futureTotal) : null,
  };
};
